import {
  triggerIncreaseCartLineEvent,
  triggerShowAddToCartSidePanelEvent,
} from '@shared/event/events/cart'
import type { IncreaseCartLineEvent } from '@shared/event/events/types'
import type { AddToCartOptions } from '@shared/global-actions/types'
import { useCartStore } from '@shared/global-store/stores/cart/cart'
import { useCustomerStore } from '@shared/global-store/stores/customer/customer'
import {
  addToCart as addToCartApi,
  createCart,
} from '@shared/shop-api/cart/api'
import { setCustomerCartId } from '@shared/shop-api/customer/api'
import Cookie from 'js-cookie'

const CART_COOKIE_EXPIRY_DAYS = 14

export const createEmptyCart = async (saveCartAsCookie = true) => {
  const customerStore = useCustomerStore()

  const cart = await createCart()

  if (saveCartAsCookie) {
    Cookie.set('fo_cart', cart.id, {
      path: '/',
      expires: CART_COOKIE_EXPIRY_DAYS,
      sameSite: 'Lax',
      secure: import.meta.env.PROD,
    })
  }

  if (customerStore.customer?.id) {
    await setCustomerCartId(customerStore.customer.id, cart.id)
  }

  return cart
}

export const addToCart = async (
  variantId: number,
  quantity = 1,
  options: Partial<AddToCartOptions> = {}
) => {
  const cartStore = useCartStore()

  try {
    let { cart } = cartStore

    cartStore.setIsProcessing(true)

    if (!cart) {
      cart = await createEmptyCart(options.saveCartAsCookie ?? true)
    }

    const newCart = await addToCartApi(
      cart.id,
      [{ variantId, quantity, attributes: options.attributes }],
      options.cartAttributes
    )

    const cartLine = newCart.lines.find(
      (cartLine) => cartLine.variant.id === variantId
    )

    if (!cartLine) {
      throw 'Could not add line to cart'
    }

    const increaseCartLineEvent: IncreaseCartLineEvent = {
      cart: newCart,
      quantity,
      cartLine,
      added: !cartStore.cart?.lines.some(
        (cartLine) => cartLine.variant.id === variantId
      ),
    }

    await options.beforeTriggerIncreaseCartLineEvent?.(increaseCartLineEvent)

    triggerIncreaseCartLineEvent(increaseCartLineEvent)

    cartStore.setCart(newCart)

    if (options.showSidePanel) {
      triggerShowAddToCartSidePanelEvent({
        cart: newCart,
        cartLine,
      })
    }

    cartStore.setIsProcessing(false)

    return increaseCartLineEvent
  } catch (error) {
    cartStore.setIsProcessing(false)
    throw error
  }
}

export const cartActions = {
  createEmptyCart,
  addToCart,
}
